import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Loader2, Search, ArrowUpDown, Smartphone } from 'lucide-react';
import Pagination from "@/components/ui/PaginationWrapper";

function ManageGroupPopup({ text, closePopup, manageEmailPopup, manageGroupPopup }) {
    const [loading, setLoading] = useState(true);
    const [conditionalLoader, setConditionalLoader] = useState(false);
    const [activePage, setActivePage] = useState(1);
    const [userList, setUserList] = useState([]);
    const [totalPage, setTotalPage] = useState(0);
    const [searchBoxValue, setSearchBoxValue] = useState('');
    const [dataNotFound, setDataNotFound] = useState('');
    const [sortColumn, setSortColumn] = useState('CreatedOn');
    const [sortOrder, setSortOrder] = useState('desc');
    const [error, setError] = useState('');
    const [isMobile, setIsMobile] = useState(false);

    // Detect mobile screen
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 768);
        };
        
        checkMobile();
        window.addEventListener('resize', checkMobile);
        
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    const userdata = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('user'));
        } catch {
            return null;
        }
    }, []);

    const fetchData = useCallback(async (pageIndex = '1', search = '', column = sortColumn, order = sortOrder) => {
        if (!userdata) return;

        setLoading(true);
        setDataNotFound('');

        try {
            const response = await fetch('/api/admin/ManageGroupNumberPagingWise', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    roleName: userdata.paramsname,
                    roleType: userdata.userrole,
                    PagingIndex: pageIndex,
                    Search: search,
                    Coloumshorting: column,
                    shortingwise: order
                })
            });

            const apidata = await response.json();

            if (apidata?.length > 0 && parseInt(apidata[0].total) !== 0) {
                setUserList(apidata[0].sp_Records);
                setTotalPage(parseInt(apidata[0].total));
                setActivePage(parseInt(pageIndex));
            } else {
                setUserList([]);
                setTotalPage(0);
                setDataNotFound('No records found');
            }
        } catch (err) {

            setUserList([]);
            setTotalPage(0);
            setError('Please try later');
        } finally {
            setLoading(false);
            setConditionalLoader(false);
        }
    }, [userdata, sortColumn, sortOrder]);

    useEffect(() => {
        fetchData('1', '', 'CreatedOn', 'desc');
    }, []);

    const handlePageChange = useCallback((pageNumber) => {
        fetchData(pageNumber.toString(), searchBoxValue, sortColumn, sortOrder);
    }, [fetchData, searchBoxValue, sortColumn, sortOrder]);

    const handleSearch = useCallback(() => {
        setConditionalLoader(true);
        fetchData('1', searchBoxValue, sortColumn, sortOrder);
    }, [fetchData, searchBoxValue, sortColumn, sortOrder]);

    const handleSearchKeyDown = useCallback((e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSearch();
        }
    }, [handleSearch]);

    const handleSort = useCallback((column) => {
        const newOrder = sortColumn === column && sortOrder === 'asc' ? 'desc' : 'asc';
        setSortColumn(column);
        setSortOrder(newOrder);
        fetchData('1', searchBoxValue, column, newOrder);
    }, [sortColumn, sortOrder, fetchData, searchBoxValue]);

    const handleManageEmail = useCallback((item) => {
        manageEmailPopup?.(item.groupNumber, item.whId, item.wholeSeller);
        closePopup?.();
    }, [manageEmailPopup, closePopup]);

    const handleManageProfile = useCallback((item) => {
        manageGroupPopup?.(item.groupNumber, item.wholeSeller);
        closePopup?.();
    }, [manageGroupPopup, closePopup]);

    const handleOpenChange = useCallback((open) => {
        if (!open) {
            // Reset state when closing
            setSearchBoxValue('');
            setActivePage(1);
            setSortColumn('CreatedOn');
            setSortOrder('desc');
            closePopup();
        }
    }, [closePopup]);

        return (
        <Dialog open={true} onOpenChange={handleOpenChange}>
            <DialogContent className="sm:max-w-4xl h-[calc(100vh-3rem)] [&>div]:!p-0">
                <div className="flex flex-col h-full px-4 sm:px-6">
                    <DialogHeader className="flex-shrink-0 pt-4 sm:pt-6">
                        <DialogTitle>{text}</DialogTitle>
                    </DialogHeader>

                    {loading ? (
                        <div className="flex-1 flex justify-center items-center">
                            <Loader2 className="h-8 w-8 animate-spin text-primary" />
                        </div>
                    ) : isMobile ? (
                        <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                            <Smartphone className="h-16 w-16 text-muted-foreground mb-4" />
                            <h3 className="text-lg font-semibold text-foreground mb-2">
                                Content Not Available
                            </h3>
                            <p className="text-sm text-muted-foreground">
                                Content not available for mobile screen. Please use a desktop or tablet device.
                            </p>
                        </div>
                    ) : (
                        <>
                            {/* Fixed Search Section */}
                            <div className="flex-shrink-0 flex gap-2">
                                <div className="relative flex-1">
                                    <Input
                                        type="text"
                                        placeholder="Search"
                                        value={searchBoxValue}
                                        onChange={(e) => setSearchBoxValue(e.target.value)}
                                        onKeyDown={handleSearchKeyDown}
                                        disabled={loading}
                                    />
                                </div>
                                <Button 
                                    onClick={handleSearch}
                                    disabled={loading || conditionalLoader}
                                >
                                    {conditionalLoader ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                        <>
                                            <Search className="h-4 w-4 mr-2" />
                                            Search
                                        </>
                                    )}
                                </Button>
                            </div>

                            {/* Scrollable Table */}
                            {userList.length > 0 && (
                                <>
                                    <div className="flex-1 overflow-y-auto min-h-0 mt-4 rounded-md border">
                                        <Table>
                                            <TableHeader>
                                                <TableRow>
                                                    <TableHead 
                                                        className="cursor-pointer hover:bg-muted/50"
                                                        onClick={() => handleSort('GroupNumber')}
                                                    >
                                                        <div className="flex items-center gap-1">
                                                            Group Number
                                                            <ArrowUpDown className="h-4 w-4" />
                                                        </div>
                                                    </TableHead>
                                                    <TableHead 
                                                        className="cursor-pointer hover:bg-muted/50"
                                                        onClick={() => handleSort('WholeSeller')}
                                                    >
                                                        <div className="flex items-center gap-1">
                                                            Wholesaler Name
                                                            <ArrowUpDown className="h-4 w-4" />
                                                        </div>
                                                    </TableHead>
                                                    <TableHead>Wholesaler Commission($)</TableHead>
                                                    <TableHead 
                                                        className="cursor-pointer hover:bg-muted/50"
                                                        onClick={() => handleSort('CreatedOn')}
                                                    >
                                                        <div className="flex items-center gap-1">
                                                            Created On
                                                            <ArrowUpDown className="h-4 w-4" />
                                                        </div>
                                                    </TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {userList.map((item, index) => (
                                                    <TableRow key={index}>
                                                        <TableCell className="font-medium">
                                                            {item.groupNumber}
                                                        </TableCell>
                                                        <TableCell>{item.wholeSeller}</TableCell>
                                                        <TableCell>{item.wholeSellerCommsion}</TableCell>
                                                        <TableCell>{item.createdOn}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </div>

                                    {/* Fixed Pagination */}
                                    <div className="flex-shrink-0 flex justify-center mt-4 px-4 sm:px-6 pb-4 sm:pb-6">
                                        <Pagination
                                            activePage={activePage}
                                            itemsCountPerPage={10}
                                            totalItemsCount={totalPage}
                                            pageRangeDisplayed={5}
                                            onChange={handlePageChange}
                                        />
                                    </div>
                                </>
                            )}

                            {/* Error */}
                            {error && (
                                <p className="flex-shrink-0 text-sm text-destructive mt-2 px-4 sm:px-6">{error}</p>
                            )}
                        </>
                    )}
                </div>
            </DialogContent>
        </Dialog>
        );
}

export default ManageGroupPopup;
